/**
 * /admin/health — Glance-able status board for ops.
 *
 * Pulls from the single `/api/admin/health` endpoint and refreshes
 * every 30s. Designed so a foreman or admin can spot a dead scheduler
 * / stuck closeout queue / DB hiccup without tailing logs.
 */
import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import Breadcrumb from "@/components/Breadcrumb";
import HealthTrends from "@/components/HealthTrends";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Activity, AlertCircle, Database, Loader2, Clock, RefreshCw, CheckCircle2 } from "lucide-react";

function StatCard({ label, value, sub, tone = "neutral", testid }) {
  const tones = {
    good: "border-emerald-900/60 bg-emerald-950/30 text-emerald-100",
    warn: "border-amber-900/60 bg-amber-950/30 text-amber-100",
    bad: "border-red-900/60 bg-red-950/30 text-red-100",
    neutral: "border-slate-800 bg-slate-900 text-slate-100",
  };
  return (
    <Card className={tones[tone]}>
      <CardContent className="p-4" data-testid={testid}>
        <div className="text-[10px] tracking-[0.2em] uppercase font-display opacity-70">{label}</div>
        <div className="text-2xl font-display font-black mt-1">{value}</div>
        {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function fmtAge(iso) {
  if (!iso) return "never";
  try {
    const t = new Date(iso).getTime();
    const ms = Date.now() - t;
    if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
    return `${Math.round(ms / 86_400_000)}d ago`;
  } catch {
    return iso;
  }
}

export default function HealthPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/health");
      setData(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load health");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const queue = data?.queue || {};
  const stuck = (queue.pending || 0) + (queue.running || 0);
  const failures = data?.recent_errors_24h?.failed_closeout_jobs || 0;
  const sweepIso = data?.last_reminder_sweep;
  const sweepAgeMs = sweepIso ? Date.now() - new Date(sweepIso).getTime() : Infinity;
  const sweepTone = sweepAgeMs > 6 * 3600_000 ? "warn" : "good";
  const durations = data?.closeout_durations || {};

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <Breadcrumb items={[{ label: "Admin", to: "/admin" }, { label: "Health" }]} />
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="health-heading">System Health</h1>
          <div className="text-xs text-slate-400 mt-1">
            {busy ? "Refreshing…" : data?.checked_at ? `Updated ${fmtAge(data.checked_at)}` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          data-testid="health-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 bg-slate-900 hover:bg-slate-800 text-xs font-display tracking-wider uppercase"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {err && (
        <div className="rounded-md border border-red-900 bg-red-950/40 text-red-200 p-3 text-sm mb-4 flex items-center gap-2" data-testid="health-error">
          <AlertCircle className="w-4 h-4" />{err}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
            <StatCard
              label="Mongo"
              value={data.mongo_ok ? "OK" : "DOWN"}
              tone={data.mongo_ok ? "good" : "bad"}
              testid="health-mongo"
            />
            <StatCard
              label="Closeout Queue"
              value={stuck}
              sub={`${queue.pending || 0} pending · ${queue.running || 0} running · ${queue.completed || 0} done`}
              tone={stuck > 5 ? "warn" : "neutral"}
              testid="health-queue"
            />
            <StatCard
              label="Scheduler Last Run"
              value={fmtAge(sweepIso)}
              sub={sweepIso ? new Date(sweepIso).toLocaleString() : "never fired — scheduler down?"}
              tone={sweepIso ? sweepTone : "warn"}
              testid="health-scheduler"
            />
            <StatCard
              label="Failed Jobs (24h)"
              value={failures}
              sub={`${data.recent_errors_24h?.login_failed_events || 0} login fails · ${data.recent_errors_24h?.locked_users || 0} locked users`}
              tone={failures > 0 ? "warn" : "good"}
              testid="health-errors"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4" data-testid="health-durations">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-blue-400" />
                  <div className="font-display font-bold tracking-tight">Closeout PDF Generation Times</div>
                </div>
                {durations.samples ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    <div><div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Median</div><div className="font-mono text-blue-300">{durations.median_seconds}s</div></div>
                    <div><div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">P95</div><div className="font-mono text-amber-300">{durations.p95_seconds}s</div></div>
                    <div><div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Min</div><div className="font-mono text-slate-300">{durations.min_seconds}s</div></div>
                    <div><div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Max</div><div className="font-mono text-slate-300">{durations.max_seconds}s</div></div>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">No completed closeout jobs yet.</div>
                )}
                <div className="text-[11px] text-slate-500 mt-2">Based on last {durations.samples || 0} completed jobs.</div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-4" data-testid="health-collections">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-3.5 h-3.5 text-emerald-400" />
                  <div className="font-display font-bold tracking-tight">Collection Counts</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(data.collection_counts || {}).map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{k.replace(/_/g, " ")}</div>
                      <div className="font-mono">{v}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mb-4">
            <Activity className="w-3 h-3" /> Auto-refreshes every 30 seconds.
          </div>

          <div className="flex items-center gap-2 mb-3 mt-2">
            <h2 className="font-display font-black tracking-tight text-lg" data-testid="health-trends-heading">7-day Trends</h2>
            <span className="text-[11px] text-slate-500">hourly snapshots</span>
          </div>
          <HealthTrends hours={168} />
        </>
      )}
    </AppShell>
  );
}
